import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getAllPages, filterPagesForAutocomplete } from '../utils/wikiLinkParser';

/**
 * Autocomplete dropdown for wiki links
 * Shows when user types [[ and filters pages as they type
 */
export default function WikiLinkAutocomplete({
  isOpen,
  query,
  sections,
  onSelect,
  onClose,
  position = { top: 0, left: 0 }
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [safePosition, setSafePosition] = useState(position);
  const dropdownRef = useRef(null);
  const itemRefs = useRef([]);

  // Get all pages and filter based on query
  const allPages = getAllPages(sections || []);
  const filteredPages = filterPagesForAutocomplete(query || '', allPages);

  const handleSelect = useCallback((page) => {
    if (onSelect) {
      // Use the display format: Section > Page
      onSelect(page.displayText);
    }
  }, [onSelect]);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    setSafePosition({ top: position.top, left: position.left });
  }, [position.left, position.top]);

  // Scroll selected item into view
  useEffect(() => {
    if (itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;

    const reposition = () => {
      const dropdownEl = dropdownRef.current;
      if (!dropdownEl) return;

      const parentEl = dropdownEl.offsetParent;
      const parentWidth = parentEl?.clientWidth || window.innerWidth;
      const parentHeight = parentEl?.clientHeight || window.innerHeight;
      const dropdownWidth = dropdownEl.offsetWidth || 320;
      const dropdownHeight = dropdownEl.offsetHeight || 220;
      const margin = 8;

      const nextLeft = Math.min(
        Math.max(position.left, margin),
        Math.max(margin, parentWidth - dropdownWidth - margin)
      );
      const nextTop = Math.min(
        Math.max(position.top, margin),
        Math.max(margin, parentHeight - dropdownHeight - margin)
      );

      setSafePosition({ top: nextTop, left: nextLeft });
    };

    reposition();
    window.addEventListener('resize', reposition);
    return () => window.removeEventListener('resize', reposition);
  }, [isOpen, position.left, position.top, filteredPages.length]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredPages.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredPages[selectedIndex]) {
          handleSelect(filteredPages[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (onClose) onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredPages, onClose, handleSelect]);

  if (!isOpen || filteredPages.length === 0) {
    return null;
  }

  return (
    <div
      ref={dropdownRef}
      className="absolute z-[10000] bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card shadow-elevated max-h-64 overflow-y-auto"
      style={{
        top: `${safePosition.top}px`,
        left: `${safePosition.left}px`,
        minWidth: '250px',
        maxWidth: '400px'
      }}
    >
      {filteredPages.map((page, index) => (
        <button
          key={`${page.sectionId}-${page.pageId}`}
          ref={el => itemRefs.current[index] = el}
          onClick={() => handleSelect(page)}
          className={`w-full text-left px-3 py-2 text-sm transition-colors duration-150 ${
            index === selectedIndex
              ? 'bg-[#3B82F6] text-white'
              : 'text-[#111827] dark:text-[#F9FAFB] hover:bg-[#F3F4F6] dark:hover:bg-[#374151]'
          }`}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="font-medium">{page.pageName}</div>
          <div className={`text-xs ${
            index === selectedIndex
              ? 'text-blue-100'
              : 'text-[#6B7280] dark:text-[#9CA3AF]'
          }`}>
            {[page.folderName, page.sectionName].filter(Boolean).join(' > ')}
          </div>
        </button>
      ))}
    </div>
  );
}
